import { AxiosInstance } from "axios"
import { Shop, ShopDetailsType, ShopItem, ShopTypes, ShopHardware, PaymentMethod, HardwareOrder } from "./shopsTypes"
import { ServiceResponse } from "../../../../core/api/apiTypes"

export enum ShopsQuery {
    LIST = "shops-for-merchant",
    LIST_SUBSCRIPTIONS = "subscription-types-for-merchant",
    DETAILS = "details-for-shop",
    HARDWARE_PACKAGES = "hardware-packages-for-shop",
    PAYMENT_METHODS = "payment-methods-for-shop",
}

// -- Fetch Products --
type ShopsListParams = Readonly<{
    merchantId: number
}>

export type ShopsListResponse = {
    shops: Shop[]
}

export type MerchantSubscriptionsResponse = Readonly<{
    subscriptionTypes: { id: number; displayName: string }[]
}>

export async function fetchShopsList(
    { merchantId }: ShopsListParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ShopsListResponse> {
    const url = `/merchants/${merchantId}/shops`

    const response = await axios.get(url, {
        params: {},
        signal,
    })

    return response.data
}

type DeleteShopParams = Readonly<{
    shopId: number
}>

export async function deleteShop(
    { shopId }: DeleteShopParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.delete(`/shops/${shopId}`, {
        signal,
    })
    return data
}

type ShopDetailsParam = Readonly<{
    shopId: string
}>

export type ShopDetailsResponse = Readonly<{
    shop: ShopDetailsType
}>

export async function shopDetails(
    { shopId }: ShopDetailsParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ShopDetailsResponse> {
    const url = `/shops/${shopId}`

    const { data } = await axios.get(url, {
        signal,
    })

    return data
}

export async function createShop(
    shop: ShopItem,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/shops`,
        {
            shop,
        },
        {
            signal,
        },
    )
    return data
}

type UpdateShopParam = Readonly<{
    shopId: string
    shop: ShopItem
}>

export async function updateShop(
    { shopId, shop }: UpdateShopParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/shops/${shopId}`,
        {
            shop,
        },
        {
            signal,
        },
    )
    return data
}

// Hardware Packages
type ShopTypeParam = Readonly<{
    merchantId: number
    shopType?: ShopTypes
}>
export type HardwarePackagesResponse = Readonly<{
    shopHardware: ShopHardware
}>

export async function hardwarePackagesList(
    { merchantId, shopType }: ShopTypeParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<HardwarePackagesResponse> {
    const url = `/merchants/${merchantId}/shops/hardwarePackages`

    const { data } = await axios.get(url, {
        params: {
            shopType,
        },
        signal,
    })

    return data
}

type CreateHardwareOrderParam = Readonly<{
    order: HardwareOrder
}>

export async function createHardwareOrder(
    { order }: CreateHardwareOrderParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        "orders/gopay/hardware",
        {
            ...order,
        },
        {
            signal,
        },
    )
    return data
}
// END Hardware Packages

export type PaymentMethodsResponse = Readonly<{
    paymentMethods: PaymentMethod[]
}>

export async function paymentMethodsList(
    { merchantId, shopType }: ShopTypeParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<PaymentMethodsResponse> {
    const url = `/merchants/${merchantId}/payments/paymentMethods`

    const { data } = await axios.get(url, {
        params: {
            shopType,
        },
        signal,
    })

    return data
}

export async function fetchMerchantSubscriptions(
    { merchantId }: ShopsListParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MerchantSubscriptionsResponse> {
    const url = `/merchants/${merchantId}/shops/subscriptionTypes`
    const response = await axios.get(url, { signal })

    return response.data
}
